import React, { useState, useRef, useEffect } from "react";
import { Loader2, ChevronDown, Upload } from "lucide-react";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../contexts/AuthContext";

function ImportExportPage() {
  const [type, setType] = useState(null);
  const [dimension, setDimension] = useState(null);
  const [reference, setReference] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [availableFiles, setAvailableFiles] = useState([]);
  const fileInputRef = useRef(null);
  const { fetchWithAuth } = useAuth();

  // Fetch available files on component mount
  useEffect(() => {
    const fetchAvailableFiles = async () => {
      try {
        const response = await fetchWithAuth(`/stage/list`);
        if (response.ok) {
          const files = await response.json();
          setAvailableFiles(files);
        }
      } catch (error) {
        console.error("Failed to fetch available files:", error);
      }
    };

    fetchAvailableFiles();
  }, [fetchWithAuth]);

  // Get available dimensions based on selected type
  const getAvailableDimensions = () => {
    if (!type || !availableFiles.length) return [];

    const filteredFiles = availableFiles.filter((file) => file.type === type);
    const dimensions = [
      ...new Set(filteredFiles.map((file) => file.dimension)),
    ];
    return dimensions.sort();
  };

  // Get available references based on selected type and dimension
  const getAvailableReferences = () => {
    if (!type || !dimension || !availableFiles.length) return [];

    const filteredFiles = availableFiles.filter(
      (file) => file.type === type && file.dimension === dimension
    );
    const references = [
      ...new Set(filteredFiles.map((file) => file.referential).filter(Boolean)),
    ];
    return references.sort();
  };

  // Mock data - replace with actual API calls later
  const dimensions = getAvailableDimensions();
  const references = getAvailableReferences();

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setDimension(null);
    setReference(null);
  };

  const handleDimensionChange = (e) => {
    setDimension(e.target.value);
    setReference(null);
  };

  const handleReferenceChange = (e) => {
    setReference(e.target.value);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 3000);
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      let url;
      if (type === "translation") {
        url = `${API_BASE_URL}/translation/dimension/${dimension}/ref/${reference}`;
      } else if (type === "stage") {
        url = `${API_BASE_URL}/stage/dimension/${dimension}`;
      } else {
        showMessage("Invalid type", "error");
        setIsDownloading(false);
        return;
      }

      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download =
        type === "translation"
          ? `DWH.DIM_${dimension}_${reference}_IMPORT.xlsx`
          : `DIM_${dimension}_STAGE.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      showMessage("Failed to download file", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    try {
      setIsUploading(true);

      let url;
      if (type === "translation") {
        url = `${API_BASE_URL}/translation/dimension/${dimension}/ref/${reference}`;
      } else {
        url = `${API_BASE_URL}/stage/dimension/${dimension}`;
      }

      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchWithAuth(url, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");

      showMessage("File uploaded successfully", "success");
      // Reset file input if it exists
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      showMessage("Failed to upload file", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadDisabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploadDisabled) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploadDisabled || isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Check if file is Excel
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        handleUpload(file);
      } else {
        showMessage("Please upload an Excel file (.xlsx or .xls)", "error");
      }
    }
  };

  const downloadDisabled = !dimension || (type === "translation" && !reference);
  const uploadDisabled = !dimension || (type === "translation" && !reference);

  return (
    <div className="p-6 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
      <h1 className="text-2xl font-bold mb-6 dark:text-white">
        Import/Export Data
      </h1>

      <div className="flex flex-wrap gap-4 mb-6">
        <h3 className="text-lg ">Choices </h3>
        <select
          className="p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48"
          value={type || ""}
          onChange={handleTypeChange}
        >
          <option value="" disabled>
            Select Type
          </option>
          <option value="translation">Translation Mapping</option>
          <option value="stage">Stage Mapping</option>
        </select>

        <select
          className={`p-2 border border-gray-300 dark:border-gray-600 rounded w-48 ${
            !type
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
              : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          }`}
          value={dimension || ""}
          onChange={handleDimensionChange}
          disabled={!type}
        >
          <option value="" disabled>
            Select Dimension
          </option>
          {dimensions.map((dim) => (
            <option key={dim} value={dim}>
              {dim}
            </option>
          ))}
        </select>

        {type === "translation" && (
          <select
            className={`p-2 border border-gray-300 dark:border-gray-600 rounded w-48 ${
              !dimension
                ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                : "bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            }`}
            value={reference || ""}
            onChange={handleReferenceChange}
            disabled={!dimension}
          >
            <option value="" disabled>
              Select Reference
            </option>
            {references.map((ref) => (
              <option key={ref} value={ref}>
                {ref}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex gap-4 mb-6 justify-center">
        <button
          onClick={handleDownload}
          disabled={downloadDisabled || isDownloading}
          className={` min-w-[150px] px-4 py-2 rounded flex items-center justify-center ${
            downloadDisabled || isDownloading
              ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              : "bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700"
          }`}
        >
          <ChevronDown className="h-4 w-4 mr-2" />
          {isDownloading ? (
            <>
              <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
              Downloading...
            </>
          ) : (
            "Download"
          )}
        </button>
      </div>
      <h1 className="text-center dark:text-gray-400 mb-6 text-lg">or</h1>
      {/* File Drop Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
          isDragging
            ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
            : uploadDisabled || isUploading
            ? "border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed"
            : "border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600"
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="fileUpload"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          disabled={uploadDisabled || isUploading}
          className="hidden"
          accept=".xlsx,.xls"
        />

        {isUploading ? (
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="animate-spin h-8 w-8 text-blue-500 dark:text-blue-400 mb-2" />
            <p className="text-gray-600 dark:text-gray-300">
              Uploading file...
            </p>
          </div>
        ) : (
          <div className={uploadDisabled ? "opacity-50" : ""}>
            <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <p className="text-gray-600 dark:text-gray-300 mb-1">
              Upload your Excel file here, or
            </p>
            <label
              htmlFor="fileUpload"
              className={` min-w-[150px] inline-block px-4 py-2 rounded ${
                uploadDisabled
                  ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 dark:bg-blue-600 text-white hover:bg-blue-600 dark:hover:bg-blue-700 cursor-pointer"
              }`}
            >
              <Upload className="inline-block w-4 h-4 mr-1 -mt-1" />
              Upload
            </label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Supports: .xlsx, .xls
            </p>
          </div>
        )}
      </div>
      {message.text && (
        <div
          className={`p-3 rounded ${
            message.type === "success"
              ? "bg-green-100 dark:bg-green-900 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300"
              : "bg-red-100 dark:bg-red-900 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300"
          }`}
        >
          {message.text}
        </div>
      )}
    </div>
  );
}

export default ImportExportPage;
